import { useTranslation } from 'react-i18next'
import { Download, Umbrella } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { Button } from '@/components/shadcn/button'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { EmployeeUserResponse } from '@/api/model'
import type { AbsenceSummaryState } from '../hooks/useAbsenceSummaries'

interface Props {
  employees: EmployeeUserResponse[]
  summariesById: Map<string, AbsenceSummaryState>
  isLoading: boolean
  isError: boolean
  onRowClick?: (employee: EmployeeUserResponse) => void
  /** Row button "show current vacation". */
  onShowQuota?: (employee: EmployeeUserResponse) => void
  /** Row button "export absences". */
  onExport?: (employee: EmployeeUserResponse) => void
}

/** 8 data columns + 2 columns for the row buttons. */
const COL_COUNT = 10

/** Whole number without a decimal place, half days with one decimal place. */
function formatDays(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

/**
 * Dumb table of the absence overview: renders only rows + row buttons and
 * reports clicks upward via callbacks (dialog/view state lives in the caller
 * `AbsenceOverviewTable`). Pattern like `EmployeesDataTable`.
 */
export function AbsenceOverviewDataTable({
  employees,
  summariesById,
  isLoading,
  isError,
  onRowClick,
  onShowQuota,
  onExport,
}: Props) {
  const { t } = useTranslation(['service-modules-absence', 'common'])
  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <TableHead>{t('columns.firstName', { ns: 'common' })}</TableHead>
          <TableHead>{t('columns.lastName', { ns: 'common' })}</TableHead>
          <TableHead className="text-right">{t('absence.overview.remainingVacation')}</TableHead>
          <TableHead className="text-right">{t('absence.overview.surplus')}</TableHead>
          <TableHead className="text-right">{t('absence.overview.illness')}</TableHead>
          <TableHead className="text-right">{t('absence.overview.planned')}</TableHead>
          <TableHead className="text-right">{t('absence.overview.taken')}</TableHead>
          <TableHead className="text-right">{t('absence.overview.pendingApprovals')}</TableHead>
          <TableHead className="w-10" />
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable
          isLoading={isLoading}
          isError={isError}
          isEmpty={employees.length === 0}
          colSpan={COL_COUNT}
          errorMessage={t('absence.overview.loadError')}
          emptyMessage={t('absence.overview.empty')}
        >
          {employees.map((employee) => {
          const state = employee.id ? summariesById.get(employee.id) : undefined
          const summary = state?.summary

          /** Day-value cell: loading dots during the fan-out, "—" when there's no value. */
          const dayCell = (value: number | null | undefined) => {
            if (state?.isPending) return '…'
            if (state?.isError || value == null) return '—'
            return t('absence.overview.days', { days: formatDays(value) })
          }

          return (
            <TableRow
              key={employee.id}
              onClick={onRowClick ? () => onRowClick(employee) : undefined}
              className={onRowClick ? 'cursor-pointer' : undefined}
            >
              <TableCell>{employee.firstName || '—'}</TableCell>
              <TableCell>{employee.lastName || '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{dayCell(summary?.remainingVacationDays)}</TableCell>
              <TableCell className="text-right tabular-nums">
                {state?.isPending ? '…' : summary?.surplusFromPrevious == null ? '—' : formatDays(summary.surplusFromPrevious)}
              </TableCell>
              <TableCell className="text-right tabular-nums">{dayCell(summary?.illnessDays)}</TableCell>
              <TableCell className="text-right tabular-nums">{dayCell(summary?.plannedVacationDays)}</TableCell>
              <TableCell className="text-right tabular-nums">{dayCell(summary?.takenVacationDays)}</TableCell>
              <TableCell className="text-right tabular-nums">
                {state?.isPending ? '…' : summary?.pendingApprovals ?? '—'}
              </TableCell>
              {/* rowButton1: show current vacation */}
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('absence.overview.showQuota')}
                  title={t('absence.overview.showQuota')}
                  onClick={() => onShowQuota?.(employee)}
                >
                  <Umbrella className="size-4 text-primary" />
                </Button>
              </TableCell>
              {/* rowButton2: export absences */}
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('absence.overview.exportRow')}
                  title={t('absence.overview.exportRow')}
                  onClick={() => onExport?.(employee)}
                >
                  <Download className="size-4" />
                </Button>
              </TableCell>
            </TableRow>
          )
          })}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
